import React, { useState, useEffect } from 'react';
import { getOrderDetails, updateOrderStatus, resetOrderStatus, cancelOrder, generateDeliveryNote, downloadOrderSummary, dispatchOrderWithPickedQuantities, pickOrderWithQuantities, getOrderPickStatus, updateOrderNotes } from '../../api/orders';
import { uploadOrderImage, getOrderImageUrl, deleteOrderImage } from '../../api/orderImages';
import { generate_pick_note } from '../../api/enhancedWarehouse';
import { useAlert } from '../../providers/AlertProvider';
import CustomerPaperworkUpload from './CustomerPaperworkUpload';
import CustomerPaperworkViewer from './CustomerPaperworkViewer';
import EnhancedPickingModal from './EnhancedPickingModal';
import QRViewModal from './QRViewModal';
import DispatchSignatureModal from './DispatchSignatureModal';
import { is } from 'date-fns/locale';
import { isMobile, isTablet } from 'react-device-detect';
import logger from "../../utils/logger";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';

/**
 * OrderDetailsModal.jsx
 * 
 * Modal component for displaying detailed order information
 */
export default function OrderDetailsModal({ orderId, realtimeOrder = null, onClose, onOrderUpdate, color, theme = "light", profile = null, isAdmin = false, isWarehouse = false, isCustomer = false, warehouseData = [] }) {
    const { showAlert } = useAlert();
    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [qrModalData, setQrModalData] = useState(null);
    const [paperworkRefreshKey, setPaperworkRefreshKey] = useState(0);
    const [showPickingModal, setShowPickingModal] = useState(false);
    const [showDispatchSignature, setShowDispatchSignature] = useState(false);
    const [pickedData, setPickedData] = useState(null);
    const [additionalCosts, setAdditionalCosts] = useState({
        supplied_pallets: 0,
        handled_pallets: 0,
        wrapped_pallets: 0,
        banded_pallets: 0
    });
    
    // Image management state
    const [selectedImage, setSelectedImage] = useState(null);
    const [orderImageUrl, setOrderImageUrl] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    
    // Notes editing state
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [editedNotes, setEditedNotes] = useState('');
    const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
    const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);

    useEffect(() => {
        if (orderId) {
            loadOrderDetails();
        }
    }, [orderId]);

    useEffect(() => {
        if (realtimeOrder?.id !== orderId) return;

        setOrder(currentOrder => currentOrder
            ? { ...currentOrder, ...realtimeOrder }
            : realtimeOrder
        );

        if (realtimeOrder.status === 'picked' || realtimeOrder.status === 'dispatched') {
            loadPickedData(orderId);
        }
    }, [realtimeOrder, orderId]);

    const loadOrderDetails = async (showLoading = true) => {
        if (showLoading) {
            setIsLoading(true);
        }
        setError(null);
        
        try {
            const response = await getOrderDetails(orderId);
            
            if (response.success) {
                setOrder(response.data.order);
                
                // If order is picked or dispatched, load the picked data
                if (response.data.order.status === 'picked' || response.data.order.status === 'dispatched') {
                    await loadPickedData(orderId);
                }
            } else {
                setError(response.message || 'Failed to load order details');
            }
        } catch (err) {
            logger.error('Error loading order details:', err);
            setError('Error loading order details');
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    };

    const loadPickedData = async (orderId) => {
        try {
            const pickResponse = await getOrderPickStatus(orderId);
            
            if (pickResponse.success && pickResponse.data.pick_status) {
                const pickStatus = pickResponse.data.pick_status;
                
                // Add reasons to picked items
                const pickedItemsWithReasons = (pickStatus.picked_items || []).map(item => {
                    const reasonKey = `${item.product_id}-${item.pallet_id}`;
                    return {
                        ...item,
                        reason: pickStatus.discrepancy_reasons?.[reasonKey] || ''
                    };
                });
                
                // Transform the picked data to match our local structure
                const transformedPickedData = {
                    pickedItems: pickedItemsWithReasons,
                    pickedAt: pickStatus.picked_at,
                    totalRequested: pickStatus.total_requested || 0,
                    totalPicked: pickStatus.total_picked || 0,
                    discrepancies: pickStatus.discrepancy_count || 0,
                    completionPercentage: pickStatus.pick_completion_percentage || 0
                };
                
                setPickedData(transformedPickedData);
            }
        } catch (err) {
            logger.error('Error loading picked data:', err);
        }
    };

    const handleResetPickingStatus = async () => {
        if (!order) return;

        showAlert({
            message: 'Are you sure you want to reset the picking status for this order? This will allow the order to be picked again from scratch.',
            type: 'warning',
            buttons: [
                {
                    label: 'Reset',
                    type: 'danger',
                    onClick: () => {
                        resetPickingStatus();
                    }
                },
                {
                    label: 'Cancel',
                    type: 'primary'
                }
            ]
        });
    };

    const resetPickingStatus = async () => {
        logger.info('Resetting picking status for order:', order.id);
        // reset is_picking and the picking_id in the order record
        setIsUpdatingStatus(true);
        try {
            const response = await resetOrderStatus(order.id);
            if (response.success) {
                showAlert({
                    message: 'Order status reset successfully! The order can now be picked again.',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                // Reload order details to show updated status
                await loadOrderDetails();
                // Notify parent component
                if (onOrderUpdate) {
                    onOrderUpdate();
                    setIsUpdatingStatus(false);
                }
            } else {
                showAlert({
                    message: response.message || 'Failed to reset order status',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (err) {
            logger.error('Error resetting picking status:', err);
            setIsUpdatingStatus(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return timeString.slice(0, 5); // Return only HH:MM
    };

    const canCancelOrder = (Profile, Order) => {

        if (!Order) {
            logger.log('No order - returning false');
            return false;
        }
        
        // Check if user has profile information
        if (!Profile) {
            logger.log('No profile - returning false');
            return false;
        }
        
        // Get user group from profile
        const userGroup = Profile.group?.toUpperCase();
        
        // Warehouse users cannot cancel any orders
        if (userGroup === 'WAREHOUSE') {
            logger.log('Warehouse user - cannot cancel');
            return false;
        }
        
        // Customers can only cancel their own pending orders
        if (userGroup === 'CUSTOMER') {
            // Check ownership - customer can only cancel their own orders
            if (Profile.customer_id && Order.customer_id && Profile.customer_id !== Order.customer_id) {
                logger.log('Customer not owner - cannot cancel');
                return false;
            }
            // Customers can only cancel pending orders
            const canCancel = Order.status === 'pending';
            logger.log('Customer can cancel pending?', canCancel);
            return canCancel;
        }
        
        // Admins can cancel pending or picked orders (but not dispatched)
        if (userGroup === 'ADMIN') {
            const canCancel = Order.status === 'pending' || Order.status === 'picked';
            return canCancel;
        }
        
        logger.log('No matching role - returning false');
        return false;
    };

    const toggleItemPicked = (index) => {
        // Deprecated - replaced with enhanced picking modal
        logger.warn('toggleItemPicked is deprecated - use enhanced picking modal instead');
    };

    const handlePickOrder = () => {
        if (!order) return;
        setShowPickingModal(true);
    };

    const handlePickingComplete = async (pickedDataFromModal) => {
        setShowPickingModal(false);
        
        if (pickedDataFromModal) {
            // Store the picked data locally
            setPickedData(pickedDataFromModal);
            
            showAlert({
                message: 'Picking data saved. Please review picked items and select pallets before completing the order.',
                type: 'success',
                buttons: [{ label: 'Close', type: 'success' }]
            });
        }
    };

    const handleCompletePickingProcess = async () => {
        if (!order || !pickedData) return;

        setIsUpdatingStatus(true);
        try {
            // Submit the complete picking data including pallet information
            const result = await pickOrderWithQuantities(order.id, pickedData.pickedItems, additionalCosts);
            
            if (result.success) {
                showAlert({
                    message: 'Order picking completed successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'success' }]
                });

                await Promise.all([
                    loadOrderDetails(false),
                    onOrderUpdate ? onOrderUpdate() : Promise.resolve()
                ]);
            } else {
                showAlert({
                    message: result.message || 'Failed to complete order picking',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }]
                });
            }
        } catch (error) {
            logger.error('Error completing picking process:', error);
            showAlert({
                message: 'An error occurred while completing the picking process',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }]
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDispatchOrder = async (dispatchEvidence = { source: 'web' }) => {
        if (!order) return;

        // Check if order is picked
        if (order.status !== 'picked') {
            showAlert({
                message: 'Order must be picked before it can be dispatched.',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setIsUpdatingStatus(true);
        try {
            const response = await dispatchOrderWithPickedQuantities(order.id, dispatchEvidence);

            if (response.success) {
                const dispatchData = response.data || {};
                const dispatchedOrder = {
                    ...order,
                    status: dispatchData.status || 'dispatched',
                    dispatched_at: dispatchData.dispatched_at,
                    dispatched_by: dispatchData.dispatched_by,
                    dispatched_by_name: dispatchData.dispatched_by_name,
                    dispatch_source: dispatchData.dispatch_source || dispatchEvidence.source || 'web',
                    dispatch_recipient_name: dispatchData.dispatch_recipient_name
                        ?? dispatchEvidence.recipient_name
                        ?? null,
                    dispatch_signature_data: dispatchData.dispatch_signature_data
                        ?? dispatchEvidence.signature_data
                        ?? null,
                    is_picking: dispatchData.is_picking ?? false,
                    picking_id: dispatchData.picking_id ?? null,
                    updated_at: dispatchData.updated_at || dispatchData.dispatched_at
                };

                setOrder(dispatchedOrder);

                showAlert({
                    message: 'Order dispatched successfully using picked quantities!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });

                await Promise.all([
                    loadOrderDetails(false),
                    onOrderUpdate ? onOrderUpdate(dispatchedOrder) : Promise.resolve()
                ]);
                setShowDispatchSignature(false);
            } else {
                showAlert({
                    message: response.message || 'Failed to dispatch order',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            showAlert({
                message: 'An error occurred while dispatching the order',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDispatchClick = () => {
        if (isMobile || isTablet) {
            setShowDispatchSignature(true);
            return;
        }
        handleDispatchOrder();
    };

    const handleDispatchOrderOld = async () => {
        if (!order) return;

        setIsUpdatingStatus(true);
        try {

            const response = await updateOrderStatus(order.id, 'dispatched', { 
                supplied_pallets: order?.supplied_pallets,
                wrapped_pallets: order?.wrapped_pallets,
                banded_pallets: order?.banded_pallets,
                handled_pallets: order?.handled_pallets,
            });

            if (response.success) {
                showAlert({
                    message: 'Order dispatched successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                // Reload order details to show updated status
                await loadOrderDetails();
                // Update parent component's order list
                if (onOrderUpdate) {
                    onOrderUpdate();
                }
            } else {
                showAlert({
                    message: response.message || 'Failed to dispatch order',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error dispatching order:', error);
            showAlert({
                message: 'Error dispatching order',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleCancelOrder = async () => {
        if (!order) return;

        // Role-specific confirmation messages
        let message = `Are you sure you want to cancel order ${order.order_number}? This action cannot be undone and will permanently remove the order from the database.`;
        
        const userGroup = profile?.group?.toUpperCase();
        if ((userGroup === 'ADMIN' || isAdmin) && order.status === 'picked') {
            message = `Are you sure you want to cancel order ${order.order_number}? This order has already been picked but will be permanently removed from the database. This action cannot be undone.`;
        }

        // Show confirmation dialog
        showAlert({
            message: message,
            type: 'warning',
            buttons: [
                { 
                    label: 'Cancel Order', 
                    type: 'danger',
                    onClick: async () => {
                        setIsUpdatingStatus(true);
                        try {
                            const response = await cancelOrder(order.id);
                            
                            if (response.success) {
                                showAlert({
                                    message: 'Order cancelled successfully!',
                                    type: 'success',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                                // Update parent component's order list
                                if (onOrderUpdate) {
                                    onOrderUpdate();
                                }
                                // Close the modal
                                onClose();
                            } else {
                                showAlert({
                                    message: response.message || 'Failed to cancel order',
                                    type: 'danger',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                            }
                        } catch (error) {
                            logger.error('Error cancelling order:', error);
                            showAlert({
                                message: 'Error cancelling order',
                                type: 'danger',
                                buttons: [{ label: 'Close', type: 'primary' }]
                            });
                        } finally {
                            setIsUpdatingStatus(false);
                        }
                    }
                },
                { 
                    label: 'Keep Order', 
                    type: 'primary' 
                }
            ]
        });
    };

    const handleGeneratePickNote = async () => {
        if (!order) return;

        try {
            showAlert({
                message: 'Generating pick note...',
                type: 'danger',
                buttons: [{label: 'Close', type: 'primary'}]
            });

            const response = await generate_pick_note(order.id);
            
            if (response.status === 200) {
                showAlert({
                    message: 'Pick note generated and downloaded successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            } else {
                showAlert({
                    message: response.message || 'Failed to generate pick note',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }]
                });
            }
        } catch (error) {
            logger.error('Error generating pick note:', error);
            showAlert({
                message: 'An error occurred while generating pick note',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }]
            });
        }
    };

    const canPerformAction = (action) => {
        if (isAdmin) {
            return action !== 'delete' || order.status !== 'dispatched';
        }
        if (isWarehouse) {
            return action === 'pick' || action === 'dispatch';
        }
        if (isCustomer) {
            return action === 'cancel' && order.status === 'pending' && order.customer_id === profile.customer_id;
        }
        return false;
    };

    const handleAdditionalCostsChange = (field, value) => {
        setAdditionalCosts(prev => ({
            ...prev,
            [field]: parseInt(value, 10) || 0
        }));
    };

    const handleGenerateDeliveryNote = async () => {
        if (!order) return;

        setIsUpdatingStatus(true);
        try {
            const response = await generateDeliveryNote(order.id);

            if (response.success) {
                showAlert({
                    message: 'Delivery note generated and downloaded successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            } else {
                showAlert({
                    message: response.message || 'Failed to generate delivery note',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error generating delivery note:', error);
            showAlert({
                message: 'Error generating delivery note',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDownloadOrderSummary = async () => {
        if (!order || order.status !== 'dispatched') return;

        setIsDownloadingSummary(true);
        const response = await downloadOrderSummary(order.id);
        setIsDownloadingSummary(false);
        if (!response.success) {
            showAlert({
                message: response.message || 'Unable to download order summary',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }]
            });
        }
    };

    // Image management functions
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !order) return;

        console.log('Uploading image for order:', order.id, 'File:', file);
        
        setIsUploadingImage(true);
        try {
            const response = await uploadOrderImage(order.id, file);
            
            if (response.success) {
                showAlert({
                    message: 'Image uploaded successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                
                // Reload order details to get updated image path
                await loadOrderDetails();
                
                // Clear the selected file
                setSelectedImage(null);
                event.target.value = '';
                
            } else {
                showAlert({
                    message: response.message || 'Failed to upload image',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            showAlert({
                message: 'Error uploading image',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleViewImage = async () => {
        if (!order || !order.image_path) return;

        try {
            const response = await getOrderImageUrl(order.id);
            
            if (response.success && response.data.image_url) {
                setOrderImageUrl(response.data.image_url);
                setShowImageModal(true);
            } else {
                showAlert({
                    message: response.message || 'Failed to load image',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Error loading image:', error);
            showAlert({
                message: 'Error loading image',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    const handleDeleteImage = async () => {
        if (!order || !order.image_path) return;

        showAlert({
            message: 'Are you sure you want to delete this image?',
            type: 'warning',
            buttons: [
                { 
                    label: 'Cancel', 
                    type: 'secondary',
                    onClick: () => {} // Do nothing on cancel
                },
                { 
                    label: 'Delete', 
                    type: 'danger',
                    onClick: async () => {
                        try {
                            const response = await deleteOrderImage(order.id);
                            
                            if (response.success) {
                                showAlert({
                                    message: 'Image deleted successfully!',
                                    type: 'success',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                                
                                // Reload order details to reflect the change
                                await loadOrderDetails();
                                
                            } else {
                                showAlert({
                                    message: response.message || 'Failed to delete image',
                                    type: 'danger',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                            }
                        } catch (error) {
                            console.error('Error deleting image:', error);
                            showAlert({
                                message: 'Error deleting image',
                                type: 'danger',
                                buttons: [{ label: 'Close', type: 'primary' }]
                            });
                        }
                    }
                }
            ]
        });
    };

    // Notes editing functions
    const handleEditNotes = () => {
        setEditedNotes(order?.notes || '');
        setIsEditingNotes(true);
    };

    const handleCancelEditNotes = () => {
        setIsEditingNotes(false);
        setEditedNotes('');
    };

    const handleSaveNotes = async () => {
        if (!order) return;

        setIsUpdatingNotes(true);
        try {
            const response = await updateOrderNotes(order.id, editedNotes);
            
            if (response.success) {
                showAlert({
                    message: 'Order notes updated successfully!',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                
                // Update the local order state
                setOrder(prev => ({
                    ...prev,
                    notes: editedNotes
                }));
                
                // Notify parent component to refresh its data
                if (onOrderUpdate) {
                    onOrderUpdate();
                }
                
                // Exit edit mode
                setIsEditingNotes(false);
                setEditedNotes('');
                
            } else {
                showAlert({
                    message: response.message || 'Failed to update order notes',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            console.error('Error updating order notes:', error);
            showAlert({
                message: 'Error updating order notes',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsUpdatingNotes(false);
        }
    };

    if (!orderId) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: color.panel,
                border: `3px solid ${color.border}`,
                borderRadius: 8,
                width: '98%',
                maxWidth: '1400px',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                margin: 'auto'
            }}>
                {/* Header */}
                <div style={{
                    padding: '1rem',
                    borderBottom: `1px solid ${color.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0, color: color.fg }}>
                        Order Details {order && `- ${order.order_number}`}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: color.danger,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            cursor: 'pointer',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '1rem' }}>
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <div>Loading order details...</div>
                        </div>
                    ) : error ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '2rem',
                            color: color.danger
                        }}>
                            {error}
                        </div>
                    ) : order ? (
                        <div>
                            {/* Order Information */}
                            <section style={{
                                marginBottom: '2rem',
                                border: `2px solid ${color.border}`,
                                borderRadius: 6,
                                backgroundColor: color.bg
                            }}>
                                <h4 style={{
                                    margin: 0,
                                    padding: '0.75rem',
                                    borderBottom: `1px solid ${color.border}`,
                                    backgroundColor: color.headerBg,
                                    color: color.fg
                                }}>
                                    Order Information
                                </h4>
                                <div style={{ padding: '1rem' }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '1rem',
                                        color: color.fg
                                    }}>
                                        <div>
                                            <strong>Order Number:</strong> {order.order_number}
                                        </div>
                                        <div>
                                            <strong>Status:</strong>{' '}
                                            <span style={{
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500',
                                                backgroundColor: 
                                                    (order.status === 'pending' && !order.is_picking) ? '#d39b00ff' :
                                                    (order.status === 'pending' && order.is_picking) ? 'rgb(185, 7, 255)' :
                                                    order.status === 'picked' ? color.success :
                                                    order.status === 'dispatched' ? '#2f90ffff' :
                                                    color.danger + '40',
                                                color: 
                                                    (order.status === 'pending' && !order.is_picking) ? '#ffffff' :
                                                    (order.status === 'pending' && order.is_picking) ? '#ffffff' :
                                                    order.status === 'picked' ? '#ffffff' :
                                                    order.status === 'dispatched' ? '#ffffff' :
                                                    color.danger
                                            }}>
                                                {(order.is_picking && order.status === 'pending') ? 'Picking in Progress' :
                                                order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                            </span>
                                        {(order.is_picking && order.picker) && (
                                            <>
                                                <strong style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>Picker:</strong>
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    backgroundColor: 'rgb(185, 7, 255)',
                                                    color: '#ffffff'
                                                }}>
                                                    {order.picker.name + ' ' + order.picker.surname}
                                                </span>
                                                {/* reset picking status button */}
                                                <button
                                                    onClick={handleResetPickingStatus}
                                                    style={{
                                                        marginLeft: '0.5rem',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '500',
                                                        backgroundColor: 'rgb(255, 7, 7)',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faRotate} style={{ width: '20px' }} />
                                                </button>
                                            </>
                                        )}
                                        </div>
                                        <div>
                                            <strong>Total Items:</strong> {order.total_items}
                                        </div>
                                        {(order.status === 'picked' || order.status === 'dispatched') && (
                                            <div>
                                                <strong>Picked By:</strong>{' '}
                                                {order.picked_by_user
                                                    ? `${order.picked_by_user.name} ${order.picked_by_user.surname}`.trim()
                                                    : 'User unavailable'}
                                            </div>
                                        )}
                                        <div>
                                            <strong>Created:</strong> {formatDate(order.created_at)}
                                        </div>
                                        {/* Notes section with admin editing capability */}
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem', position: 'relative' }}>
                                                <strong>Notes:</strong>
                                                {(isAdmin && order.status !== 'dispatched' && !isEditingNotes) && (
                                                    <button
                                                        onClick={handleEditNotes}
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            border: `1px solid ${color.border}`,
                                                            borderRadius: 4,
                                                            padding: '0.25rem 0.5rem',
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem',
                                                            color: color.fg,
                                                            position: 'absolute',
                                                            right: 0
                                                        }}
                                                    >
                                                        Edit Notes
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {isEditingNotes ? (
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <textarea
                                                        value={editedNotes}
                                                        onChange={(e) => setEditedNotes(e.target.value)}
                                                        placeholder="Enter order notes..."
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '80px',
                                                            padding: '0.5rem',
                                                            border: `1px solid ${color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            resize: 'vertical',
                                                            fontFamily: 'inherit'
                                                        }}
                                                    />
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        gap: '0.5rem', 
                                                        marginTop: '0.5rem',
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                        <button
                                                            onClick={handleCancelEditNotes}
                                                            disabled={isUpdatingNotes}
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                border: `1px solid ${color.border}`,
                                                                borderRadius: 4,
                                                                padding: '0.5rem 1rem',
                                                                cursor: isUpdatingNotes ? 'not-allowed' : 'pointer',
                                                                fontSize: '0.9rem',
                                                                color: color.fg,
                                                                opacity: isUpdatingNotes ? 0.6 : 1
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={handleSaveNotes}
                                                            disabled={isUpdatingNotes}
                                                            style={{
                                                                backgroundColor: color.success,
                                                                border: 'none',
                                                                borderRadius: 4,
                                                                padding: '0.5rem 1rem',
                                                                cursor: isUpdatingNotes ? 'not-allowed' : 'pointer',
                                                                fontSize: '0.9rem',
                                                                color: theme === 'dark' ? '#fff' : '#000',
                                                                opacity: isUpdatingNotes ? 0.6 : 1
                                                            }}
                                                        >
                                                            {isUpdatingNotes ? 'Saving...' : 'Save Notes'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ 
                                                    fontStyle: order?.notes ? 'normal' : 'italic',
                                                    opacity: order?.notes ? 1 : 0.7,
                                                    padding: order?.notes ? '0' : '0.5rem',
                                                    backgroundColor: order?.notes ? 'transparent' : (theme === 'dark' ? '#2d3748' : '#f8f9fa'),
                                                    borderRadius: order?.notes ? 0 : 4,
                                                    border: order?.notes ? 'none' : `1px solid ${color.border}`
                                                }}>
                                                    {order?.notes || 'No notes available for this order'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Dispatched At / Picked By */}
                                    {order?.status === 'dispatched' && order.dispatched_at && (
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            justifyContent: order.dispatch_signature_data ? 'space-between' : 'center',
                                            gap: '1.5rem',
                                            marginTop: '1rem',
                                            padding: order.dispatch_signature_data ? '0 16.6667%' : '0',
                                            boxSizing: 'border-box'
                                        }}>
                                            <div style={{
                                                flex: order.dispatch_signature_data ? '1 1 240px' : '0 1 auto',
                                                textAlign: order.dispatch_signature_data ? 'left' : 'center'
                                            }}>
                                                <div><strong>Dispatched by:</strong> {order.dispatched_by_name || 'Unknown'}</div>
                                                <div>
                                                    <strong>Dispatched at:</strong>{' '}
                                                    {new Date(order.dispatched_at).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                                <div><strong>Source:</strong> {order.dispatch_source === 'device' ? 'Device' : 'Web application'}</div>
                                                {order.dispatch_recipient_name && (
                                                    <div><strong>Recipient:</strong> {order.dispatch_recipient_name}</div>
                                                )}
                                            </div>
                                            {order.dispatch_signature_data && (
                                                <div style={{ flex: '1 1 240px', textAlign: 'center' }}>
                                                    <strong style={{ display: 'block', marginBottom: 6 }}>Recipient signature</strong>
                                                    <img
                                                        src={order.dispatch_signature_data}
                                                        alt={`Signature from ${order.dispatch_recipient_name || 'recipient'}`}
                                                        style={{
                                                            display: 'block', width: 'min(360px, 100%)', maxHeight: 150,
                                                            objectFit: 'contain', objectPosition: 'center',
                                                            background: '#ffffff', border: `1px solid ${color.border}`,
                                                            borderRadius: 4, margin: '0 auto',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Delivery Information */}
                            {(order.delivery_company || order.delivery_address || order.delivery_date) && (
                                <section style={{
                                    marginBottom: '2rem',
                                    border: `2px solid ${color.border}`,
                                    borderRadius: 6,
                                    backgroundColor: color.bg
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        padding: '0.75rem',
                                        borderBottom: `1px solid ${color.border}`,
                                        backgroundColor: color.headerBg,
                                        color: color.fg
                                    }}>
                                        Delivery Information
                                    </h4>
                                    <div style={{ padding: '1rem' }}>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '1rem',
                                            color: color.fg
                                        }}>
                                            {order.delivery_company && (
                                                <div>
                                                    <strong>Delivery Name:</strong> {order.delivery_company}
                                                </div>
                                            )}
                                            {order.delivery_date && (
                                                <div>
                                                    <strong>Delivery Date:</strong> {formatDateOnly(order.delivery_date)}
                                                </div>
                                            )}
                                            {order.delivery_time && (
                                                <div>
                                                    <strong>Delivery Time:</strong> {formatTime(order.delivery_time)}
                                                </div>
                                            )}
                                            {order.collection_date && (
                                                <div>
                                                    <strong>Collection Date:</strong> {formatDateOnly(order.collection_date)}
                                                </div>
                                            )}
                                            {order.collection_courier && (
                                                <div>
                                                    <strong>Collection Courier:</strong> {order.collection_courier}
                                                </div>
                                            )}
                                            {order.delivery_address && (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <strong>Delivery Address:</strong><br />
                                                    {order.delivery_address}
                                                    {order.delivery_postcode && (
                                                        <>
                                                            <br />
                                                            <strong>Postcode:</strong> {order.delivery_postcode}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Order Items */}
                            <section style={{
                                border: `2px solid ${color.border}`,
                                borderRadius: 6,
                                backgroundColor: color.bg
                            }}>
                                <h4 style={{
                                    margin: 0,
                                    padding: '0.75rem',
                                    borderBottom: `1px solid ${color.border}`,
                                    backgroundColor: color.headerBg,
                                    color: color.fg
                                }}>
                                    Order Items
                                </h4>
                                <div style={{ padding: '1rem' }}>
                                    {order.detailed_items && order.detailed_items.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: color.headerBg }}>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'left',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Product Name</th>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'left',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Code</th>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'left',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Batch</th>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'center',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Req. Qty</th>
                                                    {pickedData && (
                                                        <>
                                                            <th style={{
                                                                padding: '0.5rem',
                                                                textAlign: 'center',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg
                                                            }}>Picked Qty</th>
                                                            <th style={{
                                                                padding: '0.5rem',
                                                                textAlign: 'left',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg
                                                            }}>Reason</th>
                                                            <th style={{
                                                                padding: '0.5rem',
                                                                textAlign: 'left',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg
                                                            }}>Scan Override</th>
                                                        </>
                                                    )}
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'left',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Pallet</th>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'left',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>Lane</th>
                                                    <th style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'center',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg
                                                    }}>QR</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {order.detailed_items.map((item, index) => {
                                                    const pickedItem = pickedData?.pickedItems?.find(p => 
                                                        p.product_id === item.product_id && p.pallet_id === item.pallet_id
                                                    );
                                                    const hasDiscrepancy = pickedItem && pickedItem.picked_quantity !== item.requested_quantity;
                                                    
                                                    return (
                                                        <tr key={index} style={{
                                                            backgroundColor: hasDiscrepancy ? (theme === 'dark' ? '#4a2729' : '#fff3cd') : 'transparent'
                                                        }}>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg,
                                                                textAlign: 'left'
                                                            }}>
                                                                {item.product_name}
                                                            </td>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                fontFamily: 'monospace',
                                                                color: color.fg,
                                                                textAlign: 'left'
                                                            }}>
                                                                {item.product_code}
                                                            </td>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg,
                                                                textAlign: 'left'
                                                            }}>
                                                                {item.batch_no || '-'}
                                                            </td>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                textAlign: 'center',
                                                                color: color.fg,
                                                            }}>
                                                                {item.requested_quantity}
                                                            </td>
                                                            {pickedData && (
                                                                <>
                                                                    <td style={{
                                                                        padding: '0.5rem',
                                                                        borderBottom: `1px solid ${color.border}`,
                                                                        textAlign: 'center',
                                                                        color: hasDiscrepancy ? (theme === 'dark' ? '#ffc107' : '#856404') : color.fg,
                                                                        fontWeight: hasDiscrepancy ? 'bold' : 'normal'
                                                                    }}>
                                                                        {pickedItem ? pickedItem.picked_quantity : 0}
                                                                    </td>
                                                                    <td style={{
                                                                        padding: '0.5rem',
                                                                        borderBottom: `1px solid ${color.border}`,
                                                                        color: hasDiscrepancy ? (theme === 'dark' ? '#ffc107' : '#856404') : color.fg,
                                                                        textAlign: 'left',
                                                                        fontStyle: pickedItem?.reason ? 'normal' : 'italic',
                                                                        opacity: pickedItem?.reason ? 1 : 0.6
                                                                    }}>
                                                                        {pickedItem?.reason || '-'}
                                                                    </td>
                                                                    <td style={{
                                                                        padding: '0.5rem',
                                                                        borderBottom: `1px solid ${color.border}`,
                                                                        color: color.fg,
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        {pickedItem?.scan_override?.pallet || pickedItem?.scan_override?.product ? (
                                                                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                                                {pickedItem.scan_override.pallet && (
                                                                                    <span style={{
                                                                                        padding: '0.2rem 0.45rem',
                                                                                        borderRadius: '12px',
                                                                                        backgroundColor: '#dc3545',
                                                                                        color: '#fff',
                                                                                        fontSize: '0.7rem',
                                                                                        fontWeight: '600'
                                                                                    }}>
                                                                                        Pallet
                                                                                    </span>
                                                                                )}
                                                                                {pickedItem.scan_override.product && (
                                                                                    <span style={{
                                                                                        padding: '0.2rem 0.45rem',
                                                                                        borderRadius: '12px',
                                                                                        backgroundColor: '#dc3545',
                                                                                        color: '#fff',
                                                                                        fontSize: '0.7rem',
                                                                                        fontWeight: '600'
                                                                                    }}>
                                                                                        Product
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        ) : '-'}
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg,
                                                                textAlign: 'left'
                                                            }}>
                                                                {item.pallet_code}
                                                            </td>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg,
                                                                textAlign: 'left'
                                                            }}>
                                                                {item.lane_id || '-'}
                                                            </td>
                                                            <td style={{
                                                                padding: '0.5rem',
                                                                borderBottom: `1px solid ${color.border}`,
                                                                textAlign: 'center'
                                                            }}>
                                                                <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                                    <button
                                                                        title="View product QR code"
                                                                        onClick={() => item.product_qr_link && setQrModalData({
                                                                            qrUrl: item.product_qr_link,
                                                                            title: 'Product QR Code',
                                                                            productId: item.product_code,
                                                                            productDescription: item.product_name
                                                                        })}
                                                                        style={{
                                                                            padding: '0.2rem 0.4rem',
                                                                            fontSize: '0.7rem',
                                                                            borderRadius: 4,
                                                                            border: 'none',
                                                                            cursor: item.product_qr_link ? 'pointer' : 'default',
                                                                            backgroundColor: item.product_qr_link ? '#198754' : '#6c757d',
                                                                            color: '#fff',
                                                                            opacity: item.product_qr_link ? 1 : 0.5
                                                                        }}
                                                                    >
                                                                        Product
                                                                    </button>
                                                                    <button
                                                                        title="View pallet QR code"
                                                                        onClick={() => item.pallet_qr_link && setQrModalData({
                                                                            qrUrl: item.pallet_qr_link,
                                                                            title: 'Pallet QR Code',
                                                                            productId: item.pallet_code,
                                                                            productDescription: `Lane: ${item.lane_id || '-'}`
                                                                        })}
                                                                        style={{
                                                                            padding: '0.2rem 0.4rem',
                                                                            fontSize: '0.7rem',
                                                                            borderRadius: 4,
                                                                            border: 'none',
                                                                            cursor: item.pallet_qr_link ? 'pointer' : 'default',
                                                                            backgroundColor: item.pallet_qr_link ? '#198754' : '#6c757d',
                                                                            color: '#fff',
                                                                            opacity: item.pallet_qr_link ? 1 : 0.5
                                                                        }}
                                                                    >
                                                                        Pallet
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '2rem',
                                            color: color.fg,
                                            opacity: 0.7
                                        }}>
                                            No items found for this order.
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Customer Paperwork - Show for all order statuses, but different permissions */}
                            <section style={{
                                border: `2px solid ${color.border}`,
                                borderRadius: 6,
                                backgroundColor: color.bg,
                                marginTop: '1rem'
                            }}>
                                <h4 style={{
                                    margin: 0,
                                    padding: '0.75rem',
                                    borderBottom: `1px solid ${color.border}`,
                                    backgroundColor: color.headerBg,
                                    color: color.fg
                                }}>
                                    Customer Paperwork
                                </h4>
                                <div style={{ padding: '1rem' }}>
                                    {/* Upload component - only show for pending orders */}
                                    {order.status === 'pending' && (
                                        <div style={{ marginBottom: '1rem' }}>
                                            <CustomerPaperworkUpload
                                                orderId={order.id}
                                                theme={theme}
                                                onUploadComplete={() => {
                                                    // Trigger a refresh of the paperwork list
                                                    setPaperworkRefreshKey(prev => prev + 1);
                                                }}
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Paperwork viewer - always show for all orders */}
                                    <CustomerPaperworkViewer
                                        key={paperworkRefreshKey}
                                        orderId={order.id}
                                        orderReference={order.reference}
                                        orderStatus={order.status}
                                        theme={theme}
                                        onPaperworkChanged={() => {
                                            // Trigger refresh when paperwork is deleted
                                            setPaperworkRefreshKey(prev => prev + 1);
                                        }}
                                    />
                                </div>
                            </section>

                            {/* Pallet Selection - Show when picking data is available but not yet submitted */}
                            {pickedData && order.status !== 'picked' && order.status !== 'dispatched' && (
                                <section style={{
                                    border: `2px solid ${color.border}`,
                                    borderRadius: 6,
                                    backgroundColor: color.bg,
                                    marginTop: '1rem'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        padding: '0.75rem',
                                        borderBottom: `1px solid ${color.border}`,
                                        backgroundColor: color.headerBg,
                                        color: color.fg
                                    }}>
                                        Pallet Processing
                                    </h4>
                                    <div style={{ padding: '1rem' }}>
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                            gap: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem',
                                                    color: color.fg,
                                                    fontWeight: '500'
                                                }}>
                                                    Supplied Pallets
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={additionalCosts.supplied_pallets}
                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                        ...prev,
                                                        supplied_pallets: parseInt(e.target.value) || 0
                                                    }))}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem',
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem',
                                                    color: color.fg,
                                                    fontWeight: '500'
                                                }}>
                                                    Handled Pallets
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={additionalCosts.handled_pallets}
                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                        ...prev,
                                                        handled_pallets: parseInt(e.target.value) || 0
                                                    }))}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem',
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem',
                                                    color: color.fg,
                                                    fontWeight: '500'
                                                }}>
                                                    Wrapped Pallets
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={additionalCosts.wrapped_pallets}
                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                        ...prev,
                                                        wrapped_pallets: parseInt(e.target.value) || 0
                                                    }))}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem',
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ 
                                                    display: 'block', 
                                                    marginBottom: '0.5rem',
                                                    color: color.fg,
                                                    fontWeight: '500'
                                                }}>
                                                    Banded Pallets
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={additionalCosts.banded_pallets}
                                                    onChange={(e) => setAdditionalCosts(prev => ({
                                                        ...prev,
                                                        banded_pallets: parseInt(e.target.value) || 0
                                                    }))}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem',
                                                        border: `1px solid ${color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div style={{
                                            backgroundColor: theme === 'dark' ? '#2d3748' : '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: 6,
                                            border: `1px solid ${color.border}`
                                        }}>
                                            <h5 style={{ 
                                                margin: '0 0 1rem 0', 
                                                color: color.fg,
                                                fontSize: '1rem'
                                            }}>
                                                Picking Summary
                                            </h5>
                                            <div style={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                                gap: '1rem',
                                                fontSize: '0.9rem'
                                            }}>
                                                <div>
                                                    <strong style={{ color: color.fg }}>Total Requested:</strong>
                                                    <div style={{ color: color.fg }}>{pickedData.totalRequested}</div>
                                                </div>
                                                <div>
                                                    <strong style={{ color: color.fg }}>Total Picked:</strong>
                                                    <div style={{ color: color.fg }}>{pickedData.totalPicked}</div>
                                                </div>
                                                <div>
                                                    <strong style={{ color: color.fg }}>Discrepancies:</strong>
                                                    <div style={{ color: pickedData.discrepancies > 0 ? '#dc3545' : color.fg }}>
                                                        {pickedData.discrepancies}
                                                    </div>
                                                </div>
                                                <div>
                                                    <strong style={{ color: color.fg }}>Completion:</strong>
                                                    <div style={{ color: color.fg }}>{pickedData.completionPercentage}%</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'flex-end', 
                                            gap: '1rem',
                                            marginTop: '1rem'
                                        }}>
                                            <button
                                                onClick={() => setPickedData(null)}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    color: color.fg,
                                                    border: `1px solid ${color.border}`,
                                                    borderRadius: 6,
                                                    padding: '0.75rem 1.5rem',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                Clear Picking Data
                                            </button>
                                            <button
                                                onClick={handleCompletePickingProcess}
                                                disabled={isUpdatingStatus}
                                                style={{
                                                    backgroundColor: color.success,
                                                    color: theme === 'dark' ? '#ffffffff' : '#000000ff',
                                                    border: 'none',
                                                    borderRadius: 6,
                                                    padding: '0.75rem 1.5rem',
                                                    cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                    fontSize: '1rem',
                                                    fontWeight: '500',
                                                    opacity: isUpdatingStatus ? 0.7 : 1
                                                }}
                                            >
                                                {isUpdatingStatus ? 'Processing...' : 'Complete Order Picking'}
                                            </button>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Order Image Section */}
                            <section style={{
                                border: `2px solid ${color.border}`,
                                borderRadius: 6,
                                backgroundColor: color.bg,
                                marginTop: '1rem'
                            }}>
                                <h4 style={{
                                    margin: 0,
                                    padding: '0.75rem',
                                    borderBottom: `1px solid ${color.border}`,
                                    backgroundColor: color.headerBg,
                                    color: color.fg
                                }}>
                                    Order Completion Photo
                                </h4>
                                <div style={{ padding: '1rem' }}>
                                    {order.image_path ? (
                                        <div style={{ 
                                            display: 'flex', 
                                            gap: '1rem', 
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{ 
                                                color: color.fg,
                                                fontSize: '0.9rem'
                                            }}>
                                                📷 Image uploaded
                                            </span>
                                            
                                            <button
                                                onClick={handleViewImage}
                                                style={{
                                                    backgroundColor: color.primary,
                                                    color: theme === 'dark' ? '#ffffffff' : '#000000ff',
                                                    border: 'none',
                                                    borderRadius: 6,
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                View Image
                                            </button>
                                            
                                            {(isAdmin || isWarehouse) && (
                                                <button
                                                    onClick={handleDeleteImage}
                                                    style={{
                                                        backgroundColor: color.danger,
                                                        color: theme === 'dark' ? '#ffffffff' : '#000000ff',
                                                        border: 'none',
                                                        borderRadius: 6,
                                                        padding: '0.5rem 1rem',
                                                        cursor: 'pointer',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    Delete Image
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            {(isAdmin || isWarehouse) ? (
                                                <div>
                                                    <p style={{ 
                                                        color: color.fg, 
                                                        margin: '0 0 1rem 0',
                                                        fontSize: '0.9rem'
                                                    }}>
                                                        Upload a completion photo for this order:
                                                    </p>
                                                    
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        disabled={isUploadingImage}
                                                        style={{
                                                            padding: '0.5rem',
                                                            border: `1px solid ${color.border}`,
                                                            borderRadius: 6,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: '0.9rem',
                                                            cursor: isUploadingImage ? 'not-allowed' : 'pointer'
                                                        }}
                                                    />
                                                    
                                                    {isUploadingImage && (
                                                        <span style={{ 
                                                            marginLeft: '1rem', 
                                                            color: color.fg,
                                                            fontSize: '0.9rem'
                                                        }}>
                                                            Uploading...
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <p style={{ 
                                                    color: color.fg, 
                                                    margin: 0,
                                                    fontSize: '0.9rem',
                                                    fontStyle: 'italic'
                                                }}>
                                                    No completion photo available for this order.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Action Buttons */}
                            {order.status !== 'dispatched' && order.status !== 'cancelled' && (
                                <section style={{
                                    border: `2px solid ${color.border}`,
                                    borderRadius: 6,
                                    backgroundColor: color.bg,
                                    marginTop: '1rem'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        padding: '0.75rem',
                                        borderBottom: `1px solid ${color.border}`,
                                        backgroundColor: color.headerBg,
                                        color: color.fg
                                    }}>
                                        Actions
                                    </h4>
                                    <div style={{ 
                                        padding: '1rem',
                                        display: 'flex',
                                        gap: '1rem',
                                        justifyContent: 'flex-start'
                                    }}>
                                        {order.status === 'pending' && (
                                            <>
                                                {(isAdmin || isWarehouse) && (
                                                    <>
                                                        <button
                                                            onClick={handlePickOrder}
                                                            disabled={isUpdatingStatus || (order.status === 'pending' && order.is_picking === true)}
                                                            style={{
                                                                backgroundColor: color.success,
                                                                color: theme === 'dark' ? '#ffffffff' : '#000000ff',
                                                                border: 'none',
                                                                borderRadius: 6,
                                                                padding: '0.75rem 1.5rem',
                                                                cursor: isUpdatingStatus || (order.status === 'pending' && order.is_picking === true) ? 'not-allowed' : 'pointer',
                                                                fontSize: '1rem',
                                                                fontWeight: '500',
                                                                opacity: isUpdatingStatus || (order.status === 'pending' && order.is_picking === true) ? 0.7 : 1
                                                            }}
                                                        >
                                                            {isUpdatingStatus ? 'Processing...' : (pickedData ? 'Update Pick' : 'Pick Order')}
                                                        </button>
                                                        
                                                        <button
                                                            onClick={handleGeneratePickNote}
                                                            disabled={isUpdatingStatus}
                                                            style={{
                                                                backgroundColor: color.primary,
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: 6,
                                                                padding: '0.75rem 1.5rem',
                                                                cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                                fontSize: '1rem',
                                                                fontWeight: '500',
                                                                opacity: isUpdatingStatus ? 0.7 : 1
                                                            }}
                                                        >
                                                            Generate Pick Note
                                                        </button>
                                                    </>
                                                )}

                                                {(isAdmin && canCancelOrder(profile, order)) && (
                                                    <button
                                                        onClick={handleCancelOrder}
                                                        disabled={isUpdatingStatus}
                                                        style={{
                                                            backgroundColor: color.danger,
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            padding: '0.75rem 1.5rem',
                                                            cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                            fontSize: '1rem',
                                                            fontWeight: '500',
                                                            opacity: isUpdatingStatus ? 0.7 : 1,
                                                        }}
                                                    >
                                                        {isUpdatingStatus ? 'Cancelling...' : 'Cancel Order'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        
                                        {order.status === 'picked' && (
                                            <>
                                                {(isAdmin || isWarehouse) && (
                                                    <button
                                                        onClick={handleDispatchClick}
                                                        disabled={isUpdatingStatus}
                                                        style={{
                                                            backgroundColor: color.primary,
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            padding: '0.75rem 1.5rem',
                                                            cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                            fontSize: '1rem',
                                                            fontWeight: '500',
                                                            opacity: isUpdatingStatus ? 0.7 : 1
                                                        }}
                                                    >
                                                        {isUpdatingStatus ? 'Dispatching...' : 'Dispatch Order'}
                                                    </button>
                                                )}
                                                
                                                {canCancelOrder(profile, order) && (
                                                    <button
                                                        onClick={handleCancelOrder}
                                                        disabled={isUpdatingStatus}
                                                        style={{
                                                            backgroundColor: color.danger,
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 6,
                                                            padding: '0.75rem 1.5rem',
                                                            cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                            fontSize: '1rem',
                                                            fontWeight: '500',
                                                            opacity: isUpdatingStatus ? 0.7 : 1,
                                                            marginRight: '10px'
                                                        }}
                                                    >
                                                        {isUpdatingStatus ? 'Cancelling...' : 'Cancel Order'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        
                                    </div>
                                </section>
                            )}

                            {/* Dispatched Order Actions */}
                            {order.status === 'dispatched' && (
                                <section style={{
                                    border: `2px solid ${color.border}`,
                                    borderRadius: 6,
                                    backgroundColor: color.bg,
                                    marginTop: '1rem'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        padding: '0.75rem',
                                        borderBottom: `1px solid ${color.border}`,
                                        backgroundColor: color.headerBg,
                                        color: color.fg
                                    }}>
                                        Delivery Actions
                                    </h4>
                                    <div style={{ 
                                        padding: '1rem',
                                        display: 'flex',
                                        gap: '0.75rem',
                                        alignItems: 'center',
                                        flexWrap: 'wrap'
                                    }}>
                                        {(isAdmin || (isCustomer && profile?.customer_id === order.customer_id)) && (
                                            <button
                                                onClick={handleDownloadOrderSummary}
                                                disabled={isDownloadingSummary}
                                                style={{
                                                    backgroundColor: color.success,
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: 6,
                                                    padding: '0.75rem 1.5rem',
                                                    cursor: isDownloadingSummary ? 'not-allowed' : 'pointer',
                                                    fontSize: '1rem',
                                                    fontWeight: '500',
                                                    opacity: isDownloadingSummary ? 0.7 : 1
                                                }}
                                            >
                                                {isDownloadingSummary ? 'Generating Summary...' : 'Download Order Summary'}
                                            </button>
                                        )}
                                        <button
                                            onClick={handleGenerateDeliveryNote}
                                            disabled={isUpdatingStatus}
                                            style={{
                                                backgroundColor: '#007bff',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 6,
                                                padding: '0.75rem 1.5rem',
                                                cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                                fontSize: '1rem',
                                                fontWeight: '500',
                                                opacity: isUpdatingStatus ? 0.7 : 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            📄 {isUpdatingStatus ? 'Generating...' : 'Generate Paperwork'}
                                        </button>
                                        
                                        <div style={{
                                            color: color.fg,
                                            fontSize: '0.875rem',
                                            opacity: 0.7
                                        }}>
                                            Generate and download delivery note PDF
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Additional Costs Summary - Shown when order is picked */}
                            {order?.status === 'picked' && (
                                <div className="additional-costs-summary" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                    <div>
                                        <strong>Pallets Supplied:</strong> {order?.supplied_pallets || 0}
                                    </div>
                                    <div>
                                        <strong>Pallets Handled:</strong> {order?.handled_pallets || 0}
                                    </div>
                                    <div>
                                        <strong>Wrapped Pallets:</strong> {order?.wrapped_pallets || 0}
                                    </div>
                                    <div>
                                        <strong>Banded Pallets:</strong> {order?.banded_pallets || 0}
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : null}
                </div>
            </div>

            {/* Enhanced Picking Modal */}
            {showPickingModal && order && (
                <EnhancedPickingModal
                    order={order}
                    existingPickData={pickedData}
                    onComplete={handlePickingComplete}
                    onCancel={() => setShowPickingModal(false)}
                    theme={theme}
                />
            )}

            <DispatchSignatureModal
                show={showDispatchSignature}
                loading={isUpdatingStatus}
                onClose={() => setShowDispatchSignature(false)}
                onConfirm={handleDispatchOrder}
                color={color}
            />

            {/* QR View Modal */}
            {qrModalData && (
                <QRViewModal
                    qrUrl={qrModalData.qrUrl}
                    title={qrModalData.title}
                    productId={qrModalData.productId}
                    productDescription={qrModalData.productDescription}
                    onClose={() => setQrModalData(null)}
                />
            )}

            {/* Image Modal */}
            {showImageModal && orderImageUrl && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                    padding: '2rem'
                }}>
                    <div style={{
                        backgroundColor: color.bg,
                        border: `2px solid ${color.border}`,
                        borderRadius: 8,
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        position: 'relative'
                    }}>
                        <div style={{
                            padding: '1rem',
                            borderBottom: `1px solid ${color.border}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: color.headerBg
                        }}>
                            <h3 style={{ 
                                margin: 0, 
                                color: color.fg 
                            }}>
                                Order Completion Photo
                            </h3>
                            <button
                                onClick={() => {
                                    setShowImageModal(false);
                                    setOrderImageUrl(null);
                                }}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: color.fg,
                                    padding: '0.25rem'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            <img
                                src={orderImageUrl}
                                alt="Order completion"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain',
                                    display: 'block',
                                    margin: '0 auto'
                                }}
                                onError={() => {
                                    showAlert({
                                        message: 'Failed to load image',
                                        type: 'danger',
                                        buttons: [{ label: 'Close', type: 'primary' }]
                                    });
                                    setShowImageModal(false);
                                    setOrderImageUrl(null);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}